import React, { useEffect, useCallback, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useParams } from 'react-router-dom';

interface Team {
  id: string;
  name: string;
  division_id: string;
}

interface Division {
  id: string;
  name: string;
}

interface DivisionSelectorProps {
  divisions: Division[];
  selectedDivision: Division | null;
  selectedTeam?: string;
  onSelectDivision: (division: Division) => void;
  onSelectTeam: (teamId: string) => void;
  teamOptions: Team[];
  className?: string;
}

const DivisionSelector: React.FC<DivisionSelectorProps> = ({
  divisions,
  selectedDivision,
  selectedTeam,
  onSelectDivision,
  onSelectTeam,
  teamOptions,
  className,
}) => {
  const isMobile = useIsMobile();
  const hasAutoSelectedTeam = useRef(false);
  const { projectSlug } = useParams();  // Get the projectSlug from URL

  const [showDivisionDropdown, setShowDivisionDropdown] = useState(false);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);

  const handleDivisionClick = useCallback(
    (division: Division) => {
      hasAutoSelectedTeam.current = false;
      onSelectDivision(division);
    },
    [onSelectDivision]
  );

  const handleTeamClick = useCallback(
    (teamId: string) => {
      onSelectTeam(teamId);
    },
    [onSelectTeam]
  );

  // Auto-select first team (once per division change) for mobile
  useEffect(() => {
    if (
      isMobile &&
      selectedDivision &&
      teamOptions.length > 0
    ) {
      const selectedTeamIsValid = teamOptions.some(t => t.id === selectedTeam);
      if (!selectedTeamIsValid) {
        onSelectTeam(teamOptions[0].id);
      }
    }
  }, [isMobile, selectedDivision, teamOptions, onSelectTeam, selectedTeam]);

  if (!divisions.length) {
    return <div className="text-sm px-4 py-2">Loading divisions…</div>;
  }

  if (isMobile) {
    return (
      <div className={cn('space-y-4', className)}>
        {/* Division dropdown */}
        <div>
          <label className="text-sm font-medium block mb-1">Select Division</label>
          <div className="w-full relative">
            <button
              className="w-full px-3 py-2 border rounded-md bg-background text-left"
              onClick={() => {
                setShowDivisionDropdown(prev => !prev);
                setShowTeamDropdown(false);
              }}
            >
              <div className="flex justify-between items-center w-full">
                <span>{selectedDivision?.name || 'Select Division'}</span>
                <svg
                  className={cn('w-4 h-4 transition-transform', showDivisionDropdown && 'rotate-180')}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            {showDivisionDropdown && (
              <div className="absolute z-10 bg-white border rounded-md shadow-md w-full mt-1">
                {divisions.map((d) => (
                  <div
                    key={d.id}
                    onClick={() => {
                      setShowDivisionDropdown(false);
                      handleDivisionClick(d);
                    }}
                    className={cn(
                      'px-3 py-2 cursor-pointer hover:bg-muted',
                      d.id === selectedDivision?.id && 'bg-[#30702F] text-white'
                    )}
                  >
                    {d.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Team dropdown */}
        {selectedDivision && teamOptions.length > 0 && (
          <div>
            <label className="text-sm font-medium block mb-1">Select Team</label>
            <div className="w-full relative">
              <button
                className="w-full px-3 py-2 border rounded-md bg-background text-left"
                onClick={() => {
                  setShowTeamDropdown(prev => !prev);
                  setShowDivisionDropdown(false);
                }}
              >
                <div className="flex justify-between items-center w-full">
                  <span>{teamOptions.find(t => t.id === selectedTeam)?.name || 'Select Team'}</span>
                  <svg
                    className={cn('w-4 h-4 transition-transform', showTeamDropdown && 'rotate-180')}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {showTeamDropdown && (
                <div className="absolute z-10 bg-white border rounded-md shadow-md w-full mt-1">
                  {teamOptions.map((t) => (
                    <div
                      key={t.id}
                      onClick={() => {
                        setShowTeamDropdown(false);
                        handleTeamClick(t.id);
                      }}
                      className={cn(
                        'px-3 py-2 cursor-pointer hover:bg-muted',
                        t.id === selectedTeam && 'bg-[#30702F] text-white'
                      )}
                    >
                      {t.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop layout
  return (
    <div className={cn('flex flex-col gap-4 w-full', className)}>
      <div className="flex gap-2 flex-wrap pb-2 overflow-x-auto">
        {divisions.map((d) => (
          <button
            key={d.id}
            onClick={() => handleDivisionClick(d)}
            className={cn(
              'px-3 py-2 rounded-md border text-sm transition-all max-w-[180px] truncate',
              d.id === selectedDivision?.id
                ? 'bg-[#30702F] text-white border-[#30702F]'
                : 'bg-muted text-foreground border-muted'
            )}
            style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)' }}
          >
            {d.name}
          </button>
        ))}
      </div>

      <div className="shrink-0 bg-border h-[1px] w-full my-2" />

      {selectedDivision && teamOptions.length > 0 && (
        <div className="flex flex-wrap gap-2 w-full">
          {teamOptions.map((t) => (
            <button
              key={t.id}
              onClick={() => handleTeamClick(t.id)}
              className={cn(
                'px-3 py-2 rounded-md border text-sm transition-all max-w-[180px] truncate',
                t.id === selectedTeam
                  ? 'bg-[#30702F] text-white border-[#30702F]'
                  : 'bg-muted text-foreground border-muted hover:bg-muted/70'
              )}
              style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)' }}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DivisionSelector;